import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  createRef,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import {
  AFPTable,
  Button,
  Pagination,
  EmptyState,
  Spinner,
  FilterTableFrame,
} from '@gsa/afp-component-library';
import PreBidReportFilters from './pre-bid-report-filter-panel';
import { useFvsPreBidSelectionsReport } from './pre-bid-report-provider';
import useDidMountEffect from '../../../hooks/use-did-mount';
import { useFederalStandards } from '../fvs-provider';
import { emDashUnicode } from '../../../utilities/constants';
import PreBidReportDownloadLink from './pre-bid-report-download-link';

const PreBidReportTable = ({ setAlert }) => {
  const navigate = useNavigate();

  const {
    selectedYear,
    selectedOptions,
    vehicleStandard,
    isApplicableFvsYearsLoading,
    isFVSOptionsForYearLoading,
  } = useFederalStandards();

  const {
    getPreBidMakeModelData,
    preBidSelectionsData,
    setFVSPreBidReportContextData,
    isPreBidSelectionLoading,
  } = useFvsPreBidSelectionsReport();

  const f = useMemo(() => {
    const defaultFilters = {
      operator: 'AND',
      value: [
        {
          key: '$preBidSelection.contract_year$',
          operator: 'EQ',
          value: selectedYear,
        },
      ],
    };

    if (!isEmpty(selectedOptions?.fedStandardCode)) {
      defaultFilters.value.push({
        key: '$preBidSelection.standardItem.fed_standard_code$',
        operator: 'IN',
        value: [selectedOptions?.fedStandardCode],
      });
    }

    if (!isEmpty(selectedOptions?.vehicleGroup)) {
      defaultFilters.value.push({
        key: '$preBidSelection.vehicle_group_code$',
        operator: 'EQ',
        value: selectedOptions?.vehicleGroup,
      });
    }

    if (!isEmpty(selectedOptions?.vehicleType)) {
      defaultFilters.value.push({
        key: '$preBidSelection.standardItem.vehicle_type$',
        operator: 'EQ',
        value: selectedOptions?.vehicleType,
      });
    }

    return defaultFilters;
  }, [selectedYear, selectedOptions]);

  const [filters, setFilters] = useState(f);
  const [order, setOrder] = useState([['preBidSelection', 'vendorId', 'ASC']]);
  const [paginationState, setPaginationState] = useState({
    limit: 10,
    offset: 0,
    currentPage: 1,
  });

  const getData = (isReset) => {
    const { limit, offset } = paginationState;
    if (isReset && offset !== 0)
      setPaginationState((prev) => ({ ...prev, offset: 0, currentPage: 1 }));
    getPreBidMakeModelData({
      variables: {
        filters,
        limit,
        offset: isReset ? 0 : offset,
        order,
      },
    });
  };

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    // Calculate new offset.
    const offset = (currentPage - 1) * itemsPerPage;
    setPaginationState({
      limit: itemsPerPage,
      offset,
      currentPage,
    });
  };

  useEffect(() => {
    getData();
    if (vehicleStandard && vehicleStandard?.status) {
      setFVSPreBidReportContextData('SET_SELECTED_STANDARDS', [
        vehicleStandard,
      ]);
    }
  }, []);

  useDidMountEffect(() => {
    getData();
  }, [order, paginationState]);

  useDidMountEffect(() => {
    getData(true);
  }, [filters]);

  const tableRef = createRef();

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: 'Vendor',
        accessor: 'preBidSelection.vendorDetail.vendorName',
        minWidth: 30,
        width: 150,
        maxWidth: 400,
        sortable: false,
        Cell: ({ row: { original } }) =>
          `${original?.preBidSelection?.vendorDetail?.vendorName}` ||
          emDashUnicode,
      },
      {
        Header: 'Intention to bid',
        accessor: 'preBidSelection.intentToBid',
        minWidth: 30,
        width: 150,
        maxWidth: 400,
        Cell: ({ value }) => (value === 1 ? 'Y' : 'N'),
      },
      {
        Header: 'Fed. Std. Code',
        accessor: 'preBidSelection.standardItem.fedStandardCode',
        minWidth: 30,
        width: 150,
        maxWidth: 400,
        Cell: ({ row: { original }, value }) =>
          `${value} - ${original?.preBidSelection?.standardItem?.fedStandard?.title}`,
      },
      {
        Header: 'Standard Item',
        accessor: 'preBidSelection.standardItemId',
        minWidth: 30,
        width: 150,
        maxWidth: 400,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row: { original } }) => {
          // eslint-disable-next-line react/prop-types
          const standardItem = original?.preBidSelection.standardItem;
          const queryParams = new URLSearchParams();
          queryParams.set('filter-year', standardItem?.year);
          queryParams.set('filter-fedStandardCode', standardItem?.fedStandardCode);
          queryParams.set('filter-standardItem', standardItem?.standardItemNumber);
          const urlForReq = `/catalog/vehicle-standards/requirements?${queryParams.toString()}`;
          return (
            <Button
              variant="unstyled"
              data-testid="pre-bid-report-standard-button"
              onClick={() => {
                navigate(urlForReq);
              }}
              label={` ${standardItem?.standardItemNumber} - ${standardItem?.title}`}
            />
          );
        },
      },
      {
        Header: 'Vehicle grouping',
        accessor: 'preBidSelection.vehicleGroupCode',
        //   cellClassName: 'afpTable-cell-wordBreak',
        minWidth: 300,
        width: 400,
        maxWidth: 600,
        Cell: ({ row: { original } }) =>
          `${original?.preBidSelection?.vehicleGroupCode} - ${original?.preBidSelection?.vehicleGroup?.title}`,
      },
    ];

    return columnList;
  }, []);

  const renderRowSubComponent = useCallback(({ row: { original } }) => {
    return (
      <div className="grid-container standard-items-sub-component-wrapper">
        <div className="grid-row grid-gap">
          <div className="tablet:grid-col-6">
            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="tablet:grid-col standard-item-title">
                Vendor company user name
              </div>
              <div className="tablet:grid-col text-right margin-left-1">
                {original?.preBidSelection?.createdByUserInfo?.fullName ||
                  emDashUnicode}
              </div>
            </div>

            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="tablet:grid-col standard-item-title">
                Vendor email
              </div>
              <div className="tablet:grid-col text-right standard-item-last-row margin-left-1">
                {original?.preBidSelection?.vendorDetail?.emailAddress ||
                  emDashUnicode}
              </div>
            </div>

            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="tablet:grid-col standard-item-title">
                Vehicle type
              </div>
              <div className="tablet:grid-col text-right margin-left-1">
                {original?.preBidSelection?.standardItem?.vehicleTypeCode.code}{' '}
                -{' '}
                {original?.preBidSelection?.standardItem?.vehicleTypeCode.title}
              </div>
            </div>
          </div>
          <div className="tablet:grid-col-6">
            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="standard-item-title">Body Make</div>
              <div className="tablet:grid-col text-right margin-left-1" />
              {original?.make?.makeName || <span>&ndash;</span>}
            </div>

            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="standard-item-title">Body Model</div>
              <div className="tablet:grid-col text-right margin-left-1">
                {original?.model?.modelName || <span>&ndash;</span>}
              </div>
            </div>

            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="standard-item-title">Chassis Make</div>
              <div className="tablet:grid-col text-right margin-left-1">
                {original?.chassisMake?.makeName || <span>&ndash;</span>}
              </div>
            </div>

            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="standard-item-title">Chassis Model</div>
              <div className="tablet:grid-col text-right margin-left-1">
                {original?.chassisModel?.modelName || <span>&ndash;</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, []);

  const TableWrapper = ({ tableProps, paginationProps }) => (
    <>
      <AFPTable {...tableProps} />
      {(isPreBidSelectionLoading ||
        isApplicableFvsYearsLoading ||
        isFVSOptionsForYearLoading) && <Spinner className="margin-y-8" />}
      {!isPreBidSelectionLoading &&
      !isApplicableFvsYearsLoading &&
      !isFVSOptionsForYearLoading &&
      tableProps.data?.length ? (
        <Pagination {...paginationProps} />
      ) : null}
      {!isPreBidSelectionLoading &&
        !isApplicableFvsYearsLoading &&
        !isFVSOptionsForYearLoading &&
        tableProps.data?.length === 0 && (
          <div className="text-center margin-top-neg-2 height-full">
            <EmptyState
              hasBackground
              containerStyles="padding-y-8 margin-top-1 height-full"
              topText={
                <div aria-label="No data available for the pre-bid summary report.">
                  <strong>
                    No data available for the pre-bid summary report.
                  </strong>
                </div>
              }
              topTextStyles="margin-top-7"
            />
          </div>
        )}
    </>
  );

  const FTF = useMemo(
    () =>
      FilterTableFrame(
        null,
        PreBidReportDownloadLink,
        PreBidReportFilters,
        TableWrapper,
      ),
    [],
  );

  return (
    <>
      <FTF
        upRightProps={{ filters, setAlert }}
        lowLeftProps={{
          currentFilters: filters,
          setFilters,
        }}
        lowRightProps={{
          tableProps: {
            columns,
            data: preBidSelectionsData.rows,
            fullWidth: true,
            expandable: true,
            ref: tableRef,
            renderRowSubComponent,
            testId: 'pre-bid-report',
            defaultSort: order,
            onSort: setOrder,
          },
          paginationProps: {
            itemsPerPageOptions: [10, 25, 50],
            onPageChange: handlePaginationChange,
            variant: 'advanced',
            itemsPerPage: paginationState.limit,
            currentPage: paginationState.currentPage,
            itemsCount: preBidSelectionsData?.count,
          },
        }}
        filterToggle
      />
    </>
  );
};

PreBidReportTable.propTypes = {
  setAlert: PropTypes.func.isRequired,
};

export default PreBidReportTable;
